import { getBaseStore } from './BaseStore';
import { getEnvironment } from './utils/utils';
import { DutyExchange } from './models/DutyExchange';
import { flow, getRoot, Instance, types } from 'mobx-state-tree';
import { RootStore } from './RootStore';
import NotificationToast from '../components/common/NotificationToast';
import moment from 'moment';
import { ApiFetcherImpl } from '../api';
import { DayOfWeek } from '../common/enums/DateAndTime';

export const DutyExchangeStore = types
  .compose(
    getBaseStore<Instance<typeof DutyExchange>, Instance<typeof DutyExchange>>(
      DutyExchange,
      'dutyExchangeId',
      'dutyExchange',
    ),
    types.model('DutyExchangeStore', {}),
  )
  .actions((self) => {
    const env = getEnvironment(self);
    const root = getRoot(self) as Instance<typeof RootStore>;

    const loadPharmacyDuties = flow(function*(pharmacyId: number) {
      const criteria = {
        pharmacyId,
        NotAssigned: false,
        startDate: moment().format(),
      };
      const result = yield Promise.all([
        root.DutyAssignmentStore!.searchAssignmentForPharmacies(criteria),
        root.PharmacyStore!.searchForDutyAssignment(criteria),
        root.DutyStore!.searchForDutyAssignment(criteria),
      ])
        .catch((e) => console.error(e))
        .then((responses) => {
          console.log(responses[0]);
          return responses[0];
        });

      return result;
    });

    const loadDateDuties = flow(function*(date: Date, pharmacyIds: number[]) {
      const criteria = {
        NotAssigned: false,
        startDate: moment(date).format(),
        endDate: moment(date).format(),
        pharmacyIds: pharmacyIds,
      };
      const result = yield Promise.all([
        root.DutyAssignmentStore!.searchAssignmentForPharmacies(criteria),
        root.PharmacyStore!.searchForDutyAssignment(criteria),
        root.DutyStore!.searchForDutyAssignment(criteria),
      ])
        .catch((e) => console.error(e))
        .then((responses) => {
          console.log(responses[0]);
          return responses[0];
        });

      return result;
    });

    const loadDayDuties = flow(function*(dayOfWeek: DayOfWeek, pharmacyIds: number[]) {
      const criteria = {
        NotAssigned: false,
        startDate: moment().format(),
        pharmacyIds: pharmacyIds,
        dayOfWeek: dayOfWeek === 6 ? 0 : dayOfWeek + 1, // API uses 0=Sunday, 1=Monday,... whereas DayOfWeek enum uses 0=Monday,...,6=Sunday
      };
      const result = yield Promise.all([
        root.DutyAssignmentStore!.searchAssignmentForPharmacies(criteria),
        root.PharmacyStore!.searchForDutyAssignment(criteria),
        root.DutyStore!.searchForDutyAssignment(criteria),
      ])
        .catch((e) => console.error(e))
        .then((responses) => {
          console.log(responses[0]);
          return responses[0];
        });

      return result;
    });

    const isInConflict = flow(function*(dutyAssignmentId: number, targetPharmacyId: number) {
      const duties = [dutyAssignmentId];
      const hasConflicts = yield env.api.post('dutyExchange/detectConflicts/' + targetPharmacyId, duties);
      return hasConflicts;
    });

    const checkDutyExchange = flow(function*(dutyExchange: Instance<typeof DutyExchange>, dutyAssignmentId: any) {
      try {
        const dutyExchangeCheck = {
          dutyExchangeDto: dutyExchange,
          dutyAssignmentId: dutyAssignmentId,
        };
        const result = yield env.api.post('dutyExchange/check', dutyExchangeCheck);
        if (Array.isArray(result)) return result;
        return [] as string[];
      } catch (e) {
        return [];
      }
    });

    const saveDutyExchange = flow(function*(
      dutyExchange: Instance<typeof DutyExchange>,
      requestorDuties: any,
      neighborDuties: any,
    ) {
      try {
        const neighborId = dutyExchange.recipientId;
        const dutyExchangeProcess = {
          dutyExchangeDto: dutyExchange,
          requestorDutyAssignments: requestorDuties,
          recipientDutyAssignments: neighborDuties,
        };
        const result = yield env.api.post('dutyExchange/process', dutyExchangeProcess);
        // const result = yield env.api.post('dutyExchange', dutyExchange);
        // const result2 = yield env.api.post('dutyExchange/' + result! + '/recipient/' + neighborId, requestorDuties);
        // const result3 = yield env.api.post('dutyExchange/' + result! + '/requestor/' + neighborId, neighborDuties);
        // const result4 = yield env.api.post('dutyExchange/' + result! + '/notifications', {});
        return result;
      } catch (e) {
        NotificationToast.showError('Error while saving data');
        return '-1';
      }
    });

    return {
      loadPharmacyDuties,
      loadDateDuties,
      loadDayDuties,
      checkDutyExchange,
      saveDutyExchange,
      isInConflict,
    };
  });
